// Set-up mode for the CTF playground: hand edits to a position. Every function returns
// a new state and leaves the input alone. The edited state is only a draft: call
// resumeAfterEdit (rules.ts) on it before playing on, which settles turns and activations.

import { hexEq } from '../board';
import { buildCells, cellIdAt, hqOf } from '../map';
import { canCarry, other, unitById, unitAt } from '../rules';
import type { CtfState, EffectId, Hex, Team } from '../types';

const copy = (s: CtfState): CtfState => {
  const { cells, log, ...rest } = s;
  return { ...JSON.parse(JSON.stringify(rest)), cells, log };
};

export const onBoard = (s: CtfState, h: Hex) => cellIdAt(s.cells, h) !== undefined;

// The flag lying on the ground at a hex, if any.
export function flagAt(s: CtfState, h: Hex): Team | null {
  return (['A', 'B'] as Team[]).find(t => !s.flags[t].carrierId && hexEq(s.flags[t].pos, h)) ?? null;
}

function dropFlag(s: CtfState, carrierId: string, at: Hex) {
  const u = unitById(s, carrierId);
  const t = other(u.team);
  u.carrying = null;
  s.flags[t] = { pos: at, carrierId: null };
}

// Put a unit on a hex (swapping with any unit already there), or take it off the board (null).
export function placeUnit(state: CtfState, unitId: string, to: Hex | null): CtfState {
  if (to && !onBoard(state, to)) return state;
  const s = copy(state);
  const u = unitById(s, unitId);
  const from = u.pos;
  if (to) {
    const there = unitAt(s, to);
    if (there && there.id !== u.id) {
      there.pos = from;
      if (!from) { there.inPrison = true; if (there.carrying) dropFlag(s, there.id, to); }
    }
    u.pos = to;
    u.inPrison = false;
    delete u.respawnRound;
  } else {
    if (u.carrying && from) dropFlag(s, u.id, from);
    u.pos = null;
    u.inPrison = true;
    u.respawnRound = s.round + 1;
  }
  return s;
}

export function turnUnit(state: CtfState, unitId: string, dir: 1 | -1): CtfState {
  const s = copy(state);
  const u = unitById(s, unitId);
  u.facing = (u.facing + (dir === 1 ? 1 : 5)) % 6;
  return s;
}

export function setActivated(state: CtfState, unitId: string, activated: boolean): CtfState {
  const s = copy(state);
  unitById(s, unitId).activated = activated;
  return s;
}

// Give the enemy flag to a unit (from the ground or another carrier), or make it drop it where it stands.
export function setCarrying(state: CtfState, unitId: string, carrying: boolean): CtfState {
  const s = copy(state);
  const u = unitById(s, unitId);
  if (!u.pos) return state;
  if (!carrying) {
    if (u.carrying) dropFlag(s, u.id, u.pos);
    return s;
  }
  if (!canCarry(s, u)) return state;
  const t = other(u.team);
  const prev = s.flags[t].carrierId;
  if (prev && prev !== u.id) unitById(s, prev).carrying = null;
  u.carrying = 'flag';
  s.flags[t] = { pos: null, carrierId: u.id };
  return s;
}

// Put a team's flag on the ground at a hex (taking it from any carrier).
export function placeFlag(state: CtfState, team: Team, to: Hex): CtfState {
  if (!onBoard(state, to)) return state;
  const s = copy(state);
  const prev = s.flags[team].carrierId;
  if (prev) unitById(s, prev).carrying = null;
  s.flags[team] = { pos: to, carrierId: null };
  return s;
}

export function returnFlag(state: CtfState, team: Team): CtfState {
  return placeFlag(state, team, hqOf(state.cells, team));
}

// Owner of a cell: neutral → A → B → neutral. Home cells never change hands.
export function cycleOwner(state: CtfState, cellId: number): CtfState {
  if (state.cells[cellId]?.home) return state;
  const s = copy(state);
  const o = s.owners[cellId];
  s.owners[cellId] = o === null ? 'A' : o === 'A' ? 'B' : null;
  return s;
}

export function setOwner(state: CtfState, cellId: number, owner: Team | null): CtfState {
  if (state.cells[cellId]?.home) return state;
  const s = copy(state);
  s.owners[cellId] = owner;
  return s;
}

export function setEffect(state: CtfState, cellId: number, effect: EffectId | null): CtfState {
  if (state.cells[cellId]?.home) return state;
  const s = copy(state);
  s.effects[cellId] = effect;
  return s;
}

// Plain fields: the round and the team to act next.
export function setFields(state: CtfState, f: Partial<{ round: number; turn: Team }>): CtfState {
  const s = copy(state);
  const n = (v: number, min: number) => Math.max(min, Math.floor(Number.isFinite(v) ? v : min));
  if (f.round !== undefined) s.round = n(f.round, 1);
  if (f.turn) { s.turn = f.turn; s.firstTeam = f.turn; }
  return s;
}

// Position text: the whole state minus the log (cells are rebuilt from the config on load).
export const POSITION_FORMAT = 'ctf6.1-position';

export function positionText(s: CtfState, seed: number): string {
  const { log: _log, cells: _cells, ...rest } = s;
  return `${POSITION_FORMAT} seed=${seed}\n${JSON.stringify(rest)}\n`;
}

export function parsePosition(text: string): { game: CtfState; seed: number } {
  const lines = text.trim().split(/\r?\n/);
  const head = lines.shift()?.trim().split(/\s+/) ?? [];
  if (head[0] !== POSITION_FORMAT) throw new Error(`Not a position (expected "${POSITION_FORMAT}" on the first line)`);
  const seed = Number((head.find(f => f.startsWith('seed=')) ?? 'seed=0').slice(5)) | 0;
  const raw = JSON.parse(lines.join('\n'));
  if (raw?.version !== 6 || !raw.config || !Array.isArray(raw.units)) throw new Error('Not a position of the current rules (or it is missing its config or units)');
  return { game: { ...raw, cells: buildCells(raw.config.rings), log: [] } as CtfState, seed };
}
